import {Inject, Injectable} from "@angular/core";
import * as pdfMake from "pdfmake/build/pdfmake";
import {TCreatedPdf} from "pdfmake/build/pdfmake";
import {
  Content,
  ContentCanvas,
  ContentColumns,
  ContentImage,
  ContentTable,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions
} from "pdfmake/interfaces";
import {Observable} from "rxjs";
import {LocalizationStrings} from "../../../i18n/i18n-model";
import {I18nService} from "../../../i18n/i18n.service";
import {ClockService} from "../../../tools/time/clock.service";
import {FormFieldConfig, translateFormValue} from "../../../tools/form/form-field-config";
import {UploadFormConfig} from "../../upload/upload-form-config";
import {emptyUploadFormModel, UploadFormModel} from "../../upload/upload-form-model";
import {convertToFullUrl, createSleepReportFonts, formatLabel, getAssetUrl, reportLogoUrl, ReportValue} from "../report";
import {AuthenticationService} from "../../authentication/authentication.service";
import {IS_TEST} from "../../../tools/test/test-token";
import {
  SleepExternalFindings,
  SleepReportData,
  SleepReportMeasurements,
  SleepReportPosition,
  SleepReportPositions,
  SleepReportSampleImage
} from "./sleep-report-data";
import {
  formatAmountWithIndex,
  formatPercentage,
  formatPercentageWithDuration,
  formatRecordedWith,
  formatReportDate,
  formatReportDateTime,
  formatReportNumber
} from "./sleep-report-formatting";
import {reportFilename} from "./sleep-report-filename";

const HEADING_COLOR = "#197D6C";
const BORDER_COLOR = "#000000";
const LINK_COLOR = "#1155cc";

const CONTENT_WIDTH = 515;
const PAGE_MARGIN = 40;
const FOOTER_HEIGHT = 110;
const FOOTER_LINE_HEIGHT = 12;
const FOOTER_LINE_Y = FOOTER_HEIGHT - PAGE_MARGIN - FOOTER_LINE_HEIGHT;
const FOOTER_COLUMN_WIDTH = CONTENT_WIDTH / 3;
const FOOTER_TOP_SPACING = 15;
const POSITIONS_FIRST_ROW_INDEX = 1;
const POSITIONS_ROW_PADDING = 1;
const POSITIONS_FIRST_ROW_TOP_PADDING = 8;
const SUMMARY_LABEL_WIDTH = 120;
const PATIENT_LABEL_WIDTH = 130;
const SLEEP_EVENTS_LABEL_WIDTH = 270;
const VALUE_LABEL_GAP = 5;
const TITLE_FONT_SIZE = 20;
const SECTION_FONT_SIZE = 13;
const TABLE_FONT_SIZE = 8;
const BODY_FONT_SIZE = 10;
const FOOTER_FONT_SIZE = 9;
const GUIDE_FONT_SIZE = 7;
const PATIENT_DATA_FONT_SIZE = 8;
const SECTION_HEADING_SPACING = 8;
const GROUP_TITLE_SPACING = 2;
const LOGO_WIDTH = 90;
const TITLE_OFFSET_TO_LOGO_TEXT = 4;

const REPORT_URL = "https://remotest.eu";

interface PositionColumn {
  label: keyof LocalizationStrings;
  position: SleepReportPosition;
}

interface PositionRow {
  label: keyof LocalizationStrings;
  value: (position: SleepReportPosition) => string;
}

interface MeasurementRow {
  label: keyof LocalizationStrings;
  value: string;
}

interface SummaryRow {
  label: keyof LocalizationStrings;
  value: string;
}

@Injectable()
export class SleepReportGenerator {
  private readonly IMAGE_WIDTH = CONTENT_WIDTH;

  private reportLanguage!: string;

  constructor(
    private readonly i18nService: I18nService,
    private readonly clockService: ClockService,
    private readonly authenticationService: AuthenticationService,
    @Inject(IS_TEST) private readonly isTest: boolean
  ) {
  }

  generateAndOpenInNewTab(sleepReportData: SleepReportData): Observable<void> {
    return new Observable((observer) =>
      this.makePdf(sleepReportData).getBlob((blob) => {
        window.open(URL.createObjectURL(blob));
        observer.next();
        observer.complete();
      }));
  }

  generateAndDownload(sleepReportData: SleepReportData): void {
    this.makePdf(sleepReportData).download(reportFilename(sleepReportData));
  }

  generate(sleepReportData: SleepReportData): Observable<File> {
    return new Observable((observer) =>
      this.makePdf(sleepReportData).getBlob((blob) => {
        observer.next(new File([blob], reportFilename(sleepReportData)));
        observer.complete();
      }));
  }

  private makePdf(sleepReportData: SleepReportData): TCreatedPdf {
    this.reportLanguage = sleepReportData.metadata.reportLanguage;

    return pdfMake.createPdf(
      this.createPdfDefinitions(sleepReportData),
      undefined,
      createSleepReportFonts());
  }

  private createPdfDefinitions(sleepReportData: SleepReportData): TDocumentDefinitions {
    return {
      info: {title: reportFilename(sleepReportData)},
      pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, FOOTER_HEIGHT],
      footer: (currentPage, pageCount) => this.createFooter(currentPage, pageCount, sleepReportData),
      content: [
        this.createSummaryPage(sleepReportData),
        this.createImagesPage(sleepReportData),
        this.createPatientDataPage(sleepReportData),
      ],
      defaultStyle: {
        font: "HelveticaNeueCondensed",
        fontSize: BODY_FONT_SIZE,
        lineHeight: 1.1,
      },
      images: {
        logo: this.logo(sleepReportData),
      },
    };
  }

  private logo(sleepReportData: SleepReportData): string {
    if (this.isTest) {
      return getAssetUrl("logo/logo-dark-green.png");
    }

    return convertToFullUrl(reportLogoUrl(
      sleepReportData.metadata.measurementId,
      this.authenticationService.getCurrentAuthenticatedUser()!.token
    ));
  }

  private createFooter(currentPage: number, pageCount: number, sleepReportData: SleepReportData): Content {
    return [
      this.createInterpretationGuide(currentPage),
      this.createFooterLine(currentPage, pageCount, sleepReportData),
    ];
  }

  private createInterpretationGuide(currentPage: number): Content {
    if (currentPage !== 1) {
      return "";
    }

    return {
      text: this.translate("sleepReportInterpretationGuide"),
      fontSize: GUIDE_FONT_SIZE,
      margin: [PAGE_MARGIN, FOOTER_TOP_SPACING, PAGE_MARGIN, 0],
    };
  }

  private createFooterLine(currentPage: number, pageCount: number, sleepReportData: SleepReportData): Content {
    return {
      columns: [
        {
          text: sleepReportData.metadata.uniqueId ?? "",
          fontSize: FOOTER_FONT_SIZE,
          width: FOOTER_COLUMN_WIDTH,
        },
        {
          text: REPORT_URL,
          link: REPORT_URL,
          color: LINK_COLOR,
          decoration: "underline",
          fontSize: FOOTER_FONT_SIZE,
          alignment: "center",
          width: FOOTER_COLUMN_WIDTH,
        },
        {
          text: [
            {text: formatLabel(this.translate("reportPageLabel"))},
            {text: `${currentPage}/${pageCount}`},
          ],
          fontSize: FOOTER_FONT_SIZE,
          alignment: "right",
          width: FOOTER_COLUMN_WIDTH,
        },
      ],
      absolutePosition: {x: PAGE_MARGIN, y: FOOTER_LINE_Y},
    };
  }

  private createSummaryPage(sleepReportData: SleepReportData): Content {
    return [
      this.createTitleRow(),
      this.createPatientAndRecordingRow(sleepReportData),
      createDivider(),
      this.createFindingsRow(sleepReportData),
      this.createPositionsTable(sleepReportData.externalFindings.measurements.positions),
      this.createMeasurementTables(sleepReportData.externalFindings.measurements),
      this.createConclusion(sleepReportData),
    ];
  }

  private createTitleRow(): ContentColumns {
    return {
      columns: [
        {
          text: this.translate("sleepReportTitle"),
          bold: true,
          fontSize: TITLE_FONT_SIZE,
          color: HEADING_COLOR,
          margin: [0, TITLE_OFFSET_TO_LOGO_TEXT, 0, 0],
        },
        {image: "logo", width: LOGO_WIDTH, alignment: "right"},
      ],
      margin: [0, 0, 0, 25],
    };
  }

  private createPatientAndRecordingRow(sleepReportData: SleepReportData): ContentColumns {
    const measurements = sleepReportData.externalFindings.measurements;
    const config = this.createUploadFormConfig(sleepReportData);

    return {
      columns: [
        this.createSummaryColumn(
          "sleepReportPatientTitle",
          [
            {label: "sleepReportLabelId", value: sleepReportData.metadata.code ?? ""},
            {label: "uploadFormLabelDateOfBirth", value: this.dateValue(config.dateOfBirth).value},
            {label: "uploadFormLabelGender", value: this.fieldValue(config.gender).value},
          ]),
        this.createSummaryColumn(
          "sleepReportRecordingTitle",
          [
            {label: "sleepReportLabelRecordedWith", value: formatRecordedWith(sleepReportData.metadata.deviceSerialNumber)},
            {label: "analysisLabelStartTime", value: this.reportDateTime(measurements.startTime)},
            {label: "sleepReportLabelDurationAfterEditing", value: measurements.totalSleepTime},
          ]),
      ],
      columnGap: 20,
    };
  }

  private createSummaryColumn(title: keyof LocalizationStrings, rows: SummaryRow[]): Content {
    return {
      stack: [
        this.createSectionHeading(title),
        ...rows.map((row) => this.createSummaryRow(row)),
      ],
    };
  }

  private createSummaryRow(row: SummaryRow): ContentColumns {
    return {
      columns: [
        {text: this.translate(row.label), width: SUMMARY_LABEL_WIDTH, bold: true},
        {text: row.value},
      ],
    };
  }

  private createSectionHeading(label: keyof LocalizationStrings): Content {
    return {
      text: this.translate(label),
      bold: true,
      fontSize: SECTION_FONT_SIZE,
      color: HEADING_COLOR,
      margin: [0, 0, 0, SECTION_HEADING_SPACING],
    };
  }

  private createFindingsRow(sleepReportData: SleepReportData): Content {
    return {
      stack: [
        {
          columns: [
            {
              text: this.translate("analysisLabelFindings"),
              width: SUMMARY_LABEL_WIDTH,
              bold: true,
              fontSize: SECTION_FONT_SIZE,
              color: HEADING_COLOR,
            },
            {
              text: `${this.translate("sleepReportLabelAhi")} = ${this.number(sleepReportData.externalFindings.measurements.ahiIndex)}`
                + `   ${this.translate("sleepReportLabelOdi3")} = ${this.number(sleepReportData.externalFindings.measurements.odi3Index)}`,
              bold: true,
              fontSize: SECTION_FONT_SIZE,
              color: HEADING_COLOR,
            },
          ],
          margin: [0, 0, 0, 10],
        },
        {text: sleepReportData.findings.findings ?? ""},
      ],
      margin: [0, 0, 0, 15],
    };
  }

  private createPositionsTable(positions: SleepReportPositions): ContentTable {
    const positionColumns: PositionColumn[] = [
      {label: "sleepReportLabelSupine", position: positions.supine},
      {label: "sleepReportLabelNotSupine", position: positions.notSupine},
      {label: "sleepReportLabelLeft", position: positions.left},
      {label: "sleepReportLabelRight", position: positions.right},
      {label: "sleepReportLabelProne", position: positions.prone},
      {label: "sleepReportLabelUpright", position: positions.upright},
    ];

    const positionRows: PositionRow[] = [
      {label: "sleepReportLabelSleepTimeDuration", value: (position) => position.sleepTimeDuration},
      {label: "sleepReportLabelSleepTimeShare", value: (position) => this.percentage(position.sleepTimePercentage)},
      {label: "sleepReportLabelAhi", value: (position) => this.number(position.ahiIndex)},
      {label: "sleepReportLabelObstructiveApneas", value: (position) => this.amountWithIndex(position.oaAmount, position.oaIndex)},
      {label: "sleepReportLabelCentralApneas", value: (position) => this.amountWithIndex(position.caAmount, position.caIndex)},
      {label: "sleepReportLabelMixedApneas", value: (position) => this.amountWithIndex(position.maAmount, position.maIndex)},
      {label: "sleepReportLabelHypopneas", value: (position) => this.amountWithIndex(position.hypoAmount, position.hypoIndex)},
      {label: "sleepReportLabelDesaturations", value: (position) => this.amountWithIndex(position.deSatAmount, position.deSatIndex)},
    ];

    return {
      table: {
        headerRows: 1,
        widths: [
          124,
          "*",
          "*",
          "*",
          "*",
          "*",
          "*"
        ],
        body: [
          [
            this.createHeadingCell("sleepReportLabelPosition"),
            ...positionColumns.map((column) => this.createHeadingCell(column.label)),
          ],
          ...positionRows.map((row) => [
            this.createLabelCell(row.label),
            ...positionColumns.map((column) => this.createValueCell(row.value(column.position), "center")),
          ]),
        ],
      },
      layout: createPositionsTableLayout(),
      margin: [0, 0, 0, 12],
    };
  }

  private createMeasurementTables(measurements: SleepReportMeasurements): ContentColumns {
    return {
      columns: [
        this.createMeasurementTable(
          [
            {label: "sleepReportLabelSpO2Average", value: this.percentage(measurements.o2Levels.spO2Average)},
            {label: "sleepReportLabelSpO2Minimum", value: this.percentage(measurements.o2Levels.spO2Minimum)},
            {label: "sleepReportLabelSpO2Baseline", value: this.percentage(measurements.o2Levels.spO2Baseline)},
            {label: "sleepReportLabelDesaturationsBelow90", value: this.pieces(measurements.o2Levels.deSatBelow90)},
            {label: "sleepReportLabelDesaturationsBelow80", value: this.pieces(measurements.o2Levels.deSatBelow80)},
            {
              label: "sleepReportLabelSpO2Below90",
              value: formatPercentageWithDuration(this.number(measurements.o2Levels.spO2Percentage), measurements.o2Levels.spO2Duration)
            },
          ],
          true),
        this.createMeasurementTable(
          [
            {label: "sleepReportLabelSnoringShare", value: this.percentage(measurements.snoringPercentage)},
            {label: "sleepReportLabelBreathingFrequency", value: measurements.averageBreathingFrequency},
            {label: "sleepReportLabelCheyneStokes", value: this.percentage(measurements.cheyneStokesPercentage)},
            {label: "sleepReportLabelHeartRateAverage", value: measurements.hrAverage},
            {label: "sleepReportLabelHeartRateMinimum", value: measurements.hrMin},
            {label: "sleepReportLabelHeartRateMaximum", value: measurements.hrMax},
          ],
          false),
      ],
      columnGap: 1,
      margin: [0, 0, 0, 15],
    };
  }

  private percentage(value: string): string {
    return formatPercentage(this.number(value));
  }

  private amountWithIndex(amount: string, index: string): string {
    return formatAmountWithIndex(amount, index, this.reportLanguage);
  }

  private number(value: string): string {
    return formatReportNumber(value, this.reportLanguage);
  }

  private createMeasurementTable(rows: MeasurementRow[], withDivider: boolean): ContentTable {
    return {
      table: {
        widths: ["*", 90],
        body: rows.map((row) => [
          this.createLabelCell(row.label),
          this.createValueCell(row.value, "right"),
        ]),
      },
      layout: createMeasurementTableLayout(withDivider),
    };
  }

  private createHeadingCell(label: keyof LocalizationStrings): TableCell {
    return {
      text: this.translate(label),
      fontSize: TABLE_FONT_SIZE,
      color: HEADING_COLOR,
      alignment: "center",
    };
  }

  private createLabelCell(label: keyof LocalizationStrings): TableCell {
    return {text: this.translate(label), fontSize: TABLE_FONT_SIZE, bold: true};
  }

  private createValueCell(value: string, alignment: "center" | "right"): TableCell {
    return {text: value, fontSize: TABLE_FONT_SIZE, alignment};
  }

  private createConclusion(sleepReportData: SleepReportData): Content {
    return {
      stack: [
        this.createSectionHeading("analysisLabelConclusion"),
        {text: sleepReportData.findings.conclusion ?? ""},
        {text: `${sleepReportData.metadata.doctor.city ?? ""} ${this.currentDate()}`.trim(), margin: [0, SECTION_HEADING_SPACING, 0, 0]},
        {text: `${sleepReportData.metadata.doctor.firstName ?? ""} ${sleepReportData.metadata.doctor.lastName ?? ""}`.trim()},
        {text: sleepReportData.metadata.doctor.title ?? ""},
      ],
      unbreakable: true,
    };
  }

  private createImagesPage(sleepReportData: SleepReportData): Content {
    return [
      this.createOxygenSaturationTrend(sleepReportData.externalFindings.images.oxygenSaturationTrend),
      this.createSampleImages(sleepReportData.externalFindings.images.sampleImages),
    ];
  }

  private createOxygenSaturationTrend(oxygenSaturationTrend: string | undefined): Content {
    if (oxygenSaturationTrend === undefined) {
      return "";
    }

    return {
      stack: [
        this.createSectionHeading("sleepReportOxygenSaturationTitle"),
        this.createImage(oxygenSaturationTrend),
      ],
      unbreakable: true,
      pageBreak: "before",
      margin: [0, 0, 0, 20],
    };
  }

  private createSampleImages(sampleImages: SleepReportSampleImage[]): Content {
    return [
      this.createSectionHeading("sleepReportRecordingExcerptsTitle"),
      ...sampleImages.map((sampleImage) => this.createSampleImage(sampleImage)),
    ];
  }

  private createSampleImage(sampleImage: SleepReportSampleImage): Content {
    return {
      stack: [this.createImage(sampleImage.image)],
      unbreakable: true,
      margin: [0, 0, 0, 15],
    };
  }

  private createImage(base64Image: string): ContentImage {
    return {image: `data:image/png;base64,${base64Image}`, width: this.IMAGE_WIDTH};
  }

  private createPatientDataPage(sleepReportData: SleepReportData): Content {
    const config = this.createUploadFormConfig(sleepReportData);

    return [
      {
        text: this.translate("reportPatientDataPageTitle"),
        fontSize: SECTION_FONT_SIZE,
        bold: true,
        color: HEADING_COLOR,
        margin: [0, 0, 0, 10],
        pageBreak: "before",
      },
      this.createFreeTextValue(this.fieldValue(config.reasonForStudy)),
      {
        columns: [
          {
            stack: [
              this.createValueGroup(
                [
                  this.fieldValue(config.gender),
                  this.dateValue(config.dateOfBirth),
                  this.fieldValue(config.height),
                  this.fieldValue(config.weight),
                  this.fieldValue(config.overweight),
                  this.fieldValue(config.profession),
                  this.numberValue("sleepReportLabelBmi", sleepReportData.metadata.bmi),
                  this.numberValue("sleepReportLabelEssScore", sleepReportData.metadata.essScore),
                ],
                PATIENT_LABEL_WIDTH),
              this.createValueGroup(
                [
                  this.fieldValue(config.smoking),
                  this.fieldValue(config.useOfSleepingPills),
                  this.fieldValue(config.alcoholConsumption),
                ],
                PATIENT_LABEL_WIDTH),
              this.createTitledValueGroup(
                "uploadFormHeaderFrequency",
                [
                  this.fieldValue(config.morningHeadaches),
                  this.fieldValue(config.breathingLoss),
                  this.fieldValue(config.fluttering),
                  this.fieldValue(config.snoring),
                  this.fieldValue(config.restlessSleep),
                  this.fieldValue(config.daytimeSleepiness),
                  this.fieldValue(config.dryMouthInMorning),
                  this.fieldValue(config.needToUrinate),
                ],
                PATIENT_LABEL_WIDTH),
            ],
          },
          {
            stack: [
              this.createValueGroup(
                [
                  this.fieldValue(config.hypertension),
                  this.fieldValue(config.diabetes),
                  this.fieldValue(config.overbite),
                  this.fieldValue(config.cloggedNasalPassage),
                  this.fieldValue(config.allergicRhinitis),
                  this.fieldValue(config.smallChin),
                ],
                PATIENT_LABEL_WIDTH),
              this.createTitledValueGroup(
                "uploadFormHeaderProbability",
                [
                  this.fieldValue(config.sittingReading),
                  this.fieldValue(config.watchingTelevision),
                  this.fieldValue(config.sittingPassive),
                  this.fieldValue(config.travelInCarForAnHour),
                  this.fieldValue(config.lyingDownInAfternoon),
                  this.fieldValue(config.sittingTalkingToSomeone),
                  this.fieldValue(config.sitInPeaceAfterNonAlcoholicLunch),
                  this.fieldValue(config.sitInCarAfterItHasStoppedForFewMinutes),
                ],
                PATIENT_LABEL_WIDTH),
            ],
          },
        ],
        columnGap: 20,
        margin: [0, 0, 0, 10],
      },
      this.createFreeTextValue(this.fieldValue(config.currentMedication)),
      this.createTitledValueGroup(
        "uploadFormHeaderSleepRegistrationEvents",
        [
          this.fieldValue(config.sleepQuality),
          this.fieldValue(config.fallingAsleepDuration),
          this.fieldValue(config.sleepHours),
          this.fieldValue(config.minutesAwakeAfterMeasurementStart),
          this.fieldValue(config.awakeningsDuringMeasurement),
        ],
        SLEEP_EVENTS_LABEL_WIDTH),
      this.createFreeTextValue(this.fieldValue(config.sleepDiary)),
      this.createFreeTextValue(this.fieldValue(config.patientFeedback)),
    ];
  }

  private createTitledValueGroup(title: keyof LocalizationStrings, values: ReportValue[], labelWidth: number): Content {
    return {
      stack: [
        this.createGroupTitle(title),
        this.createValueGroup(values, labelWidth),
      ],
    };
  }

  private createGroupTitle(title: keyof LocalizationStrings): Content {
    return {
      text: this.translate(title),
      bold: true,
      fontSize: PATIENT_DATA_FONT_SIZE,
      margin: [0, 0, 0, GROUP_TITLE_SPACING],
    };
  }

  private createValueGroup(values: ReportValue[], labelWidth: number): Content {
    return values.map((value, index) => this.createValueRow(value, labelWidth, index === values.length - 1));
  }

  private createValueRow(value: ReportValue, labelWidth: number, withMarginBottom: boolean): Content {
    return {
      columns: [
        {text: value.label, width: labelWidth, bold: true, fontSize: PATIENT_DATA_FONT_SIZE},
        {text: value.value, fontSize: PATIENT_DATA_FONT_SIZE},
      ],
      columnGap: VALUE_LABEL_GAP,
      margin: withMarginBottom ? [0, 0, 0, 10] : 0,
    };
  }

  private createFreeTextValue(value: ReportValue): Content {
    return {
      text: [
        {text: `${value.label}\n`, bold: true, fontSize: PATIENT_DATA_FONT_SIZE},
        {text: value.value, fontSize: PATIENT_DATA_FONT_SIZE},
      ],
      margin: [0, 0, 0, 10],
    };
  }

  private createUploadFormConfig(sleepReportData: SleepReportData): UploadFormConfig {
    return new UploadFormConfig({
      model: {
        ...emptyUploadFormModel(),
        ...sleepReportData.metadata.patientData,
      },
      usesDateOfBirth: true,
    });
  }

  private dateValue(config: FormFieldConfig<UploadFormModel> | undefined): ReportValue {
    if (config === undefined) {
      return {label: "", value: ""};
    }

    return {
      label: this.translate(config.label),
      value: formatReportDate(String(config.model[config.property]), this.reportLanguage),
    };
  }

  private fieldValue(config: FormFieldConfig<UploadFormModel> | undefined): ReportValue {
    if (config === undefined) {
      return {label: "", value: ""};
    }

    const value = translateFormValue(config, config.model[config.property], (key) => this.translate(key));
    const infoLabel = config.infoLabel && value ? this.translate(config.infoLabel) : undefined;

    return {
      label: this.translate(config.label),
      value: [value, infoLabel].filter((part) => !!part).join(" "),
    };
  }

  private numberValue(label: keyof LocalizationStrings, value: number | undefined): ReportValue {
    return {
      label: this.translate(label),
      value: value === undefined ? "" : `${value}`,
    };
  }

  private currentDate(): string {
    return formatReportDate(this.clockService.now().toISODate()!, this.reportLanguage);
  }

  private reportDateTime(isoDateTime: string): string {
    return formatReportDateTime(isoDateTime, this.reportLanguage);
  }

  private pieces(amount: string): string {
    return `${amount} ${this.translate("sleepReportUnitPieces")}`;
  }

  private translate(key: keyof LocalizationStrings): string {
    return this.i18nService.getLocalizedString(key, this.reportLanguage);
  }
}

function createDivider(): ContentCanvas {
  return {
    canvas: [
      {
        type: "line",
        x1: 0,
        y1: 0,
        x2: CONTENT_WIDTH,
        y2: 0,
        lineWidth: 1,
        lineColor: BORDER_COLOR,
      }
    ],
    margin: [0, 15, 0, 15],
  };
}

function createPositionsTableLayout(): CustomTableLayout {
  return {
    hLineWidth: (rowIndex) => (rowIndex === POSITIONS_FIRST_ROW_INDEX ? 0.5 : 0),
    vLineWidth: (columnIndex, node) => (isInnerColumn(columnIndex, node) ? 0.5 : 0),
    hLineColor: () => BORDER_COLOR,
    vLineColor: () => BORDER_COLOR,
    paddingTop: (rowIndex) => (rowIndex === POSITIONS_FIRST_ROW_INDEX ? POSITIONS_FIRST_ROW_TOP_PADDING : POSITIONS_ROW_PADDING),
    paddingBottom: () => POSITIONS_ROW_PADDING,
  };
}

function isInnerColumn(columnIndex: number, node: ContentTable): boolean {
  return columnIndex > 0 && columnIndex < node.table.body[0].length;
}

function createMeasurementTableLayout(withDivider: boolean): CustomTableLayout {
  return {
    hLineWidth: () => 0,
    vLineWidth: (columnIndex, node) => (withDivider && columnIndex === node.table.body[0].length ? 0.5 : 0),
    vLineColor: () => BORDER_COLOR,
    paddingTop: () => 1,
    paddingBottom: () => 1,
  };
}
